import React, { useState, useEffect, useCallback } from 'react';
import { API_PREFIX } from '../../services/api';
import ResumeActions from '../shared/ResumeViewer';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  GridLegacy as Grid,
  Chip,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Collapse,
  Slider,
  Autocomplete
} from '@mui/material';

import {
  Search,
  LocationOn,
  Work,
  Phone,
  Email,
  LinkedIn,
  GitHub,
  Refresh,
  ExpandMore,
  ExpandLess,
  PersonOutline,
  Clear,
  FilterAlt,
  ArrowUpward,
  ArrowDownward,
  School,
  Star
} from '@mui/icons-material';

const CandidateSearch = () => {
  // State Management with tag-based filters
  const [searchParams, setSearchParams] = useState({
    search: '',
    job_id: '',
    status: '',
    skills: [], // Array for tags
    locations: [], // Array for tags
    min_experience: 0,
    max_experience: 25, // FIXED: Changed from 50 to 25
    sort_by: 'processed_at',
    sort_order: 'desc'
  });

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [candidateDetailDialog, setCandidateDetailDialog] = useState({ 
    open: false, 
    candidate: null 
  });
  const [jobs, setJobs] = useState([]);
  
  // Tag input states
  const [skillInput, setSkillInput] = useState('');
  const [locationInput, setLocationInput] = useState('');

  // Skill suggestions come from the server's skill taxonomy, ordered by how many
  // candidates actually hold each one. The list below is only the starting value
  // shown before the first fetch resolves -- it used to be the entire, hardcoded
  // vocabulary, which meant the box could never offer a skill outside these 38
  // and offered plenty that no candidate had.
  const [skillSuggestions, setSkillSuggestions] = useState([
    'JavaScript', 'Python', 'Java', 'React', 'Node.js',
    'SQL', 'AWS', 'Docker', 'Kubernetes', 'Machine Learning'
  ]);

  // Terms the server could not resolve to a known skill, so a typo does not just
  // quietly narrow the results.
  const [unresolvedSkills, setUnresolvedSkills] = useState([]);

  const [locationSuggestions] = useState([
    'Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune', 'Kolkata',
    'Ahmedabad', 'Jaipur', 'Surat', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore',
    'Mohali', 'Chandigarh', 'Noida', 'Gurgaon', 'Remote', 'Work from Home',
    'New York', 'London', 'Singapore', 'Dubai', 'Toronto', 'Sydney'
  ]);

  // Load initial data
  useEffect(() => {
    loadJobs();
    performSearch();
  }, []);

  const loadJobs = async () => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/jobs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to load jobs:', error);
    }
  };

  // Enhanced search function with tag support
  const performSearch = useCallback(async (page = 1, newParams = null) => {
    setLoading(true);
    setError('');

    try {
      const paramsToUse = newParams || searchParams;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        per_page: pagination.per_page.toString(),
      });

      // Add basic parameters
      if (paramsToUse.search) queryParams.append('search', paramsToUse.search);
      if (paramsToUse.job_id) queryParams.append('job_id', paramsToUse.job_id);
      if (paramsToUse.status) queryParams.append('status', paramsToUse.status);
      if (paramsToUse.sort_by) queryParams.append('sort_by', paramsToUse.sort_by);
      if (paramsToUse.sort_order) queryParams.append('sort_order', paramsToUse.sort_order);

      // Add tag-based parameters
      if (paramsToUse.skills && paramsToUse.skills.length > 0) {
        queryParams.append('skills', paramsToUse.skills.join(','));
      }
      if (paramsToUse.locations && paramsToUse.locations.length > 0) {
        queryParams.append('location', paramsToUse.locations.join(','));
      }

      // Add experience range
      if (paramsToUse.min_experience > 0) {
        queryParams.append('min_experience', paramsToUse.min_experience.toString());
      }
      if (paramsToUse.max_experience < 25) { // FIXED: Changed from 50 to 25
        queryParams.append('max_experience', paramsToUse.max_experience.toString());
      }

      console.log('🔍 Searching with params:', Object.fromEntries(queryParams));
      
      const response = await fetch(`${API_PREFIX}/admin/candidates?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ API Response:', data);
      
      setCandidates(data.candidates || []);
      setPagination(data.pagination || {});
      setUnresolvedSkills(data.skill_query?.unresolved || []);
      
      if (data.candidates?.length === 0 && page === 1) {
        setError('No candidates found matching your criteria.');
      } else {
        setError('');
      }
    } catch (error) {
      console.error('Search error:', error);
      setError(error.message || 'Failed to load candidates. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchParams, pagination.per_page]);

  // Tag management functions
  // Debounced: one request per pause in typing, not one per keystroke.
  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        const query = skillInput.trim();
        const response = await fetch(
          `${API_PREFIX}/admin/candidates/suggestions?type=skills` +
          (query ? `&q=${encodeURIComponent(query)}` : ''),
          { credentials: 'include' }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
          setSkillSuggestions(data.suggestions);
        }
      } catch (err) {
        // Keep whatever suggestions are already on screen; an autocomplete
        // failing is not worth surfacing as a search error.
        console.debug('Skill suggestions unavailable:', err);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [skillInput]);

  const addSkillTag = (skill) => {
    if (skill && skill.trim() && !searchParams.skills.includes(skill.trim())) {
      const newSkills = [...searchParams.skills, skill.trim()];
      setSearchParams(prev => ({ ...prev, skills: newSkills }));
      setSkillInput('');
    }
  };

  const removeSkillTag = (skillToRemove) => {
    const newSkills = searchParams.skills.filter(skill => skill !== skillToRemove);
    setSearchParams(prev => ({ ...prev, skills: newSkills }));
  };

  const addLocationTag = (location) => {
    if (location && location.trim() && !searchParams.locations.includes(location.trim())) {
      const newLocations = [...searchParams.locations, location.trim()];
      setSearchParams(prev => ({ ...prev, locations: newLocations }));
      setLocationInput('');
    }
  };

  const removeLocationTag = (locationToRemove) => {
    const newLocations = searchParams.locations.filter(loc => loc !== locationToRemove);
    setSearchParams(prev => ({ ...prev, locations: newLocations }));
  };

  // Event handlers
  const handleSearch = (e) => {
    e.preventDefault();
    console.log('🔍 Search triggered with params:', searchParams);
    performSearch(1, searchParams);
  };

  const handleInputChange = (field, value) => {
    console.log(`🔧 Updating ${field}:`, value);
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePageChange = (event, newPage) => {
    performSearch(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPagination(prev => ({ ...prev, per_page: newPerPage }));
    performSearch(1);
  };

  const handleSort = (field) => {
    const newOrder = searchParams.sort_by === field && searchParams.sort_order === 'asc' ? 'desc' : 'asc';
    const newParams = {
      ...searchParams,
      sort_by: field,
      sort_order: newOrder
    };
    setSearchParams(newParams);
    performSearch(1, newParams);
  };

  const clearFilters = () => {
    const clearedParams = {
      search: '',
      job_id: '',
      status: '',
      skills: [],
      locations: [],
      min_experience: 0,
      max_experience: 25, // FIXED: Changed from 50 to 25
      sort_by: 'processed_at',
      sort_order: 'desc'
    };
    setSearchParams(clearedParams);
    setSkillInput('');
    setLocationInput('');
    performSearch(1, clearedParams);
  };

  const handleViewDetails = async (candidate) => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/candidates/${candidate.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCandidateDetailDialog({ open: true, candidate: data.candidate });
      } else {
        setCandidateDetailDialog({ open: true, candidate });
      }
    } catch (error) {
      console.error('Error fetching candidate details:', error);
      setCandidateDetailDialog({ open: true, candidate });
    }
  };

  // Status badge component
  const StatusBadge = ({ status, confidence }) => {
    const statusConfig = {
      'applied': { color: '#1976d2', label: 'Applied', bgColor: '#e3f2fd' },
      'screening': { color: '#f57c00', label: 'Pending', bgColor: '#fff3e0' },
      'interview': { color: '#1976d2', label: 'Interview', bgColor: '#e3f2fd' },
      'selected': { color: '#388e3c', label: 'Selected', bgColor: '#e8f5e8' },
      'rejected': { color: '#d32f2f', label: 'Rejected', bgColor: '#ffebee' },
      'pending': { color: '#f57c00', label: 'Pending', bgColor: '#fff3e0' }
    };
    
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    
    return (
      <Box>
        <Chip
          label={config.label}
          size="small"
          sx={{ 
            backgroundColor: config.bgColor,
            color: config.color,
            fontWeight: 600,
            fontSize: '0.75rem',
            minWidth: 70
          }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {confidence}% confidence
        </Typography>
      </Box>
    );
  };

  // Table header component
  const SortableHeader = ({ field, children }) => (
    <TableCell
      onClick={() => handleSort(field)}
      sx={{ 
        cursor: 'pointer', 
        fontWeight: 600, 
        color: 'text.secondary',
        '&:hover': { bgcolor: 'action.hover' },
        fontSize: '0.875rem'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {children}
        {searchParams.sort_by === field && (
          searchParams.sort_order === 'asc' ? 
            <ArrowUpward fontSize="small" /> : 
            <ArrowDownward fontSize="small" />
        )}
      </Box>
    </TableCell>
  );

  // Sort options for dropdown
  const sortOptions = [
    { value: 'processed_at', label: 'Recent' },
    { value: 'submitted_at', label: 'Date Applied' },
    { value: 'name', label: 'Name' },
    { value: 'total_experience_years', label: 'Experience' },
    { value: 'extraction_confidence', label: 'Confidence' }
  ];

  // Status options
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'applied', label: 'Applied' },
    { value: 'screening', label: 'Screening' },
    { value: 'interview', label: 'Interview' },
    { value: 'selected', label: 'Selected' },
    { value: 'rejected', label: 'Rejected' }
  ];

  // Check if any filters are active
  const hasActiveFilters = searchParams.search || 
                          searchParams.job_id || 
                          searchParams.status || 
                          searchParams.skills.length > 0 || 
                          searchParams.locations.length > 0 ||
                          searchParams.min_experience > 0 || 
                          searchParams.max_experience < 25; // FIXED: Changed from 50 to 25

  // Candidate Detail Dialog Component
  const CandidateDetailDialog = () => (
    <Dialog 
      open={candidateDetailDialog.open} 
      onClose={() => setCandidateDetailDialog({ open: false, candidate: null })}
      maxWidth="md"
      fullWidth
    >
      {candidateDetailDialog.candidate && (
        <>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h5">
                  {candidateDetailDialog.candidate.name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  ID: {candidateDetailDialog.candidate.id} • {candidateDetailDialog.candidate.job_title || 'Position not specified'}
                </Typography>
              </Box>
              <StatusBadge 
                status={candidateDetailDialog.candidate.current_stage} 
                confidence={candidateDetailDialog.candidate.extraction_confidence}
              />
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><Email fontSize="small" /></ListItemIcon>
                    <ListItemText primary={candidateDetailDialog.candidate.email} />
                  </ListItem>
                  {candidateDetailDialog.candidate.phone && (
                    <ListItem>
                      <ListItemIcon><Phone fontSize="small" /></ListItemIcon>
                      <ListItemText primary={candidateDetailDialog.candidate.phone} />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemIcon><LocationOn fontSize="small" /></ListItemIcon>
                    <ListItemText primary={candidateDetailDialog.candidate.location} />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Professional Details</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><Work fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary={`${candidateDetailDialog.candidate.total_experience_years || 0} years experience`} 
                    />
                  </ListItem>
                  {candidateDetailDialog.candidate.education_level && (
                    <ListItem>
                      <ListItemIcon><School fontSize="small" /></ListItemIcon>
                      <ListItemText primary={candidateDetailDialog.candidate.education_level} />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemIcon><Star fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary={`Extraction Confidence: ${candidateDetailDialog.candidate.extraction_confidence || 0}%`} 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              {candidateDetailDialog.candidate.skills && candidateDetailDialog.candidate.skills.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Skills & Technologies</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {candidateDetailDialog.candidate.skills.map((skill, index) => (
                      <Chip
                        key={index}
                        label={skill}
                        variant="outlined"
                        color="primary"
                        size="small"
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={() => setCandidateDetailDialog({ open: false, candidate: null })}>
              Close
            </Button>
            {candidateDetailDialog.candidate.linkedin_profile && (
              <Button 
                variant="outlined" 
                startIcon={<LinkedIn />}
                onClick={() => window.open(candidateDetailDialog.candidate.linkedin_profile, '_blank')}
              >
                LinkedIn
              </Button>
            )}
            {candidateDetailDialog.candidate.github_profile && (
              <Button 
                variant="outlined" 
                startIcon={<GitHub />}
                onClick={() => window.open(candidateDetailDialog.candidate.github_profile, '_blank')}
              >
                GitHub
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Search color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Candidate Management
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => performSearch(1)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Main Search Bar */}
        <Box component="form" onSubmit={handleSearch} sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search Input */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Name, email, or job title..."
                value={searchParams.search}
                onChange={(e) => handleInputChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            
            {/* Job Filter */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={searchParams.job_id}
                  onChange={(e) => handleInputChange('job_id', e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Jobs</MenuItem>
                  {jobs.map(job => (
                    <MenuItem key={job.id} value={job.id}>
                      {job.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Status Filter */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={searchParams.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: 2 }}
                >
                  {statusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Sort By */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={searchParams.sort_by}
                  onChange={(e) => handleInputChange('sort_by', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {sortOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Search Button */}
            <Grid item xs={6} md={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <Search />}
                fullWidth
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Advanced Filters Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<FilterAlt />}
            endIcon={filtersExpanded ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            sx={{ color: 'primary.main' }}
          >
            Advanced Filters
          </Button>
          
          {hasActiveFilters && (
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={clearFilters}
              color="secondary"
              sx={{ color: 'primary.main' }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Advanced Filters with Tag Support */}
        <Collapse in={filtersExpanded}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <Grid container spacing={2}>
              {/* Skills Tags */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Skills
                </Typography>
                <Autocomplete
                  freeSolo
                  options={skillSuggestions}
                  value={skillInput}
                  inputValue={skillInput}
                  onInputChange={(event, newInputValue) => {
                    setSkillInput(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      addSkillTag(newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Add skills like HTML, CSS, JavaScript..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && skillInput.trim()) {
                          e.preventDefault();
                          addSkillTag(skillInput);
                        }
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
                {/* Skills Tags Display */}
                {searchParams.skills.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {searchParams.skills.map((skill, index) => (
                      <Chip
                        key={index}
                        label={skill}
                        onDelete={() => removeSkillTag(skill)}
                        color="primary"
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                )}
              </Grid>

              {/* Location Tags */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Location
                </Typography>
                <Autocomplete
                  freeSolo
                  options={locationSuggestions}
                  value={locationInput}
                  inputValue={locationInput}
                  onInputChange={(event, newInputValue) => {
                    setLocationInput(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      addLocationTag(newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Add locations like Mumbai, Delhi, Remote..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && locationInput.trim()) {
                          e.preventDefault();
                          addLocationTag(locationInput);
                        }
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
                {/* Location Tags Display */}
                {searchParams.locations.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {searchParams.locations.map((location, index) => (
                      <Chip
                        key={index}
                        label={location}
                        onDelete={() => removeLocationTag(location)}
                        color="secondary"
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                )}
              </Grid>

              {/* Experience Range - FIXED: 0-25 years */}
              <Grid item xs={12}>
                <Box sx={{ px: 1 }}>
                  <Typography variant="subtitle2" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
                    Experience Range
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {searchParams.min_experience} - {searchParams.max_experience === 25 ? '25+' : searchParams.max_experience} years
                  </Typography>
                  <Slider
                    value={[searchParams.min_experience, searchParams.max_experience]}
                    onChange={(event, newValue) => {
                      handleInputChange('min_experience', newValue[0]);
                      handleInputChange('max_experience', newValue[1]);
                    }}
                    valueLabelDisplay="auto"
                    min={0}
                    max={25} // FIXED: Changed from 50 to 25
                    step={1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                      { value: 15, label: '15' },
                      { value: 20, label: '20' },
                      { value: 25, label: '25+' } // FIXED: Changed from 50+ to 25+
                    ]}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Skill terms the server did not recognise. Without this a typo looks
          identical to a skill nobody has -- both just return fewer results. */}
      {unresolvedSkills.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Not recognised as {unresolvedSkills.length === 1 ? 'a skill' : 'skills'}:{' '}
          <strong>{unresolvedSkills.join(', ')}</strong>
          {' \u2014 '}these were ignored. Check the spelling, or pick from the
          suggestions.
        </Alert>
      )}

      {/* Results Table - REMOVED checkbox column and contact column */}
      <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 1 }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                {/* REMOVED: Checkbox column */}
                <SortableHeader field="name">Candidate</SortableHeader>
                {/* REMOVED: Contact column */}
                <SortableHeader field="total_experience_years">Experience</SortableHeader>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Skills</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Job</TableCell>
                <SortableHeader field="processed_at">Date</SortableHeader>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}> {/* FIXED: Changed from 9 to 7 columns */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} />
                      <Typography color="text.secondary">Loading candidates...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : candidates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}> {/* FIXED: Changed from 9 to 7 columns */}
                    <Typography color="text.secondary" variant="h6">
                      No candidates found
                    </Typography>
                    <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                      Try adjusting your search criteria or filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                candidates.map((candidate) => (
                  <TableRow 
                    key={candidate.id} 
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    {/* REMOVED: Checkbox cell */}
                    
                    {/* Candidate Name & ID */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main' }}>
                          {candidate.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                            {candidate.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {candidate.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    {/* REMOVED: Contact cell */}
                    
                    {/* Experience */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Work fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight={500}>
                          {candidate.total_experience_years}y
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    {/* Skills */}
                    <TableCell>
                      {/* How many of the searched skills this candidate has.
                          Results are ordered by this, so showing it explains the
                          ordering instead of leaving it to be inferred. */}
                      {searchParams.skills.length > 0 && candidate.match_count > 0 && (
                        <Chip
                          label={`${candidate.match_count}/${
                            candidate.match_count + (candidate.missing_skills?.length || 0)
                          } matched`}
                          size="small"
                          color={candidate.match_ratio === 1 ? 'success' : 'primary'}
                          variant={candidate.match_ratio === 1 ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.6rem', height: 18, mb: 0.5,
                                '& .MuiChip-label': { px: 0.5 } }}
                          title={
                            candidate.missing_skills?.length
                              ? `Missing: ${candidate.missing_skills.join(', ')}`
                              : 'Has every skill you searched for'
                          }
                        />
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                        {candidate.skills && candidate.skills.length > 0 ? (
                          <>
                            {candidate.skills.slice(0, 3).map((skill, index) => {
                              // The server reports exactly which skills matched.
                              // Highlighting used to be a client-side substring
                              // test, which marked "JavaScript" as a match for a
                              // "Java" search -- the same bug as the old query.
                              const matched = candidate.matched_skills || [];
                              const isMatched = matched.length > 0
                                ? matched.some(m => m.toLowerCase() === skill.toLowerCase())
                                : searchParams.skills.some(searchSkill =>
                                    skill.toLowerCase() === searchSkill.toLowerCase()
                                  );
                              return (
                                <Chip
                                  key={index}
                                  label={skill}
                                  size="small"
                                  variant={isMatched ? "filled" : "outlined"}
                                  color={isMatched ? "primary" : "default"}
                                  sx={{ 
                                    fontSize: '0.65rem', 
                                    height: 20,
                                    '& .MuiChip-label': { px: 0.5 },
                                    fontWeight: isMatched ? 600 : 400
                                  }}
                                />
                              );
                            })}
                            {candidate.skills.length > 3 && (
                              <Chip
                                label={`+${candidate.skills.length - 3}`}
                                size="small"
                                variant="filled"
                                color="primary"
                                sx={{ 
                                  fontSize: '0.65rem', 
                                  height: 20,
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            )}
                          </>
                        ) : (
                          <Typography variant="caption" color="text.disabled" fontStyle="italic">
                            No skills listed
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    
                    {/* Status */}
                    <TableCell>
                      <StatusBadge 
                        status={candidate.current_stage} 
                        confidence={candidate.extraction_confidence}
                      />
                    </TableCell>
                    
                    {/* Job */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.2 }}>
                        {candidate.job_title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {candidate.vendor_name}
                      </Typography>
                    </TableCell>
                    
                    {/* Date */}
                    <TableCell>
                      <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                        {candidate.processed_at ? new Date(candidate.processed_at).toLocaleDateString('en-GB') : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Applied: {candidate.submitted_at ? new Date(candidate.submitted_at).toLocaleDateString('en-GB') : '-'}
                      </Typography>
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell>
                      {/*
                        A person glyph: this opens the candidate record, while
                        the two icons after it act on the resume file. All three
                        used to be an eye, an eye and a download.
                      */}
                      <Tooltip title="View candidate profile">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(candidate);
                          }}
                          sx={{ color: 'primary.main' }}
                        >
                          <PersonOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Resume preview / download */}
                      <Box component="span" onClick={(e) => e.stopPropagation()}>
                        <ResumeActions candidateId={candidate.id} candidateName={candidate.name} variant="icons" />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination.total_count || 0}
          rowsPerPage={pagination.per_page || 25}
          page={Math.max(0, (pagination.page || 1) - 1)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}–${to} of ${count !== -1 ? count : 'more than'} candidates` 
          }
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.25'
          }}
        />
      </Paper>

      <CandidateDetailDialog />
    </Container>
  );
};

export default CandidateSearch;
